import useSWR, { preload } from 'swr';
import {
  listFilesApi,
  createFileApi,
  updateFileApi,
  deleteFileApi,
} from 'src/api/file';
import type {
  File as ApiFile,
  ListFilesRequestContent,
  ListFilesResponseContent,
  CreateFileRequestContent,
  CreateFileResponseContent,
  UpdateFileRequestContent,
  DeleteFileRequestContent,
} from '@equaliq/api-model';

const fetcher = async (orgId?: string) => {
  const request: ListFilesRequestContent = {};
  if (orgId) {
    request.orgId = orgId;
  }

  const response: ListFilesResponseContent = await listFilesApi(request);
  return Object.values(response.files ?? {}) as ApiFile[];
};

export const preloadFiles = (orgId: string) =>
  preload(['files', orgId], () => fetcher(orgId)).catch(() => undefined);

export const useFileOperations = (orgId?: string) => {
  const swrKey = orgId ? ['files', orgId] : null;

  const { data, error, isLoading, mutate } = useSWR(swrKey, () => fetcher(orgId));

  const files = data ?? [];

  const refreshFiles = async () => {
    await mutate();
  };

  const createFile = async (
    request: CreateFileRequestContent
  ): Promise<CreateFileResponseContent> => {
    const result = await createFileApi(request);
    await mutate();
    return result as CreateFileResponseContent;
  };

  const updateFile = async (request: UpdateFileRequestContent) => {
    await updateFileApi(request);
    await mutate();
  };

  const deleteFile = async (fileId: string) => {
    const request: DeleteFileRequestContent = { fileId };
    await mutate(
      files.filter((file) => file.fileId !== fileId),
      false
    );

    try {
      await deleteFileApi(request);
      await mutate();
    } catch (errorDuringDelete) {
      await mutate();
      throw errorDuringDelete;
    }
  };

  return {
    files,
    isLoading,
    error,
    refreshFiles,
    createFile,
    updateFile,
    deleteFile,
  };
};
